<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { UiButton, UiCard, UiIcon, UiText, useApi } from "@shopexperts/shared";
import { captureConvexError } from "../telemetry";

type Failure = "missing" | "unavailable" | "error";

const api = useApi();
const route = useRoute();
const router = useRouter();
const expertSlug = computed(() => (typeof route.query.expert === "string" ? route.query.expert.trim() : ""));
const failure = ref<Failure | null>(null);
const opening = ref(false);

const quoteHref = computed(() => `/get-free-quote?${new URLSearchParams({ expert: expertSlug.value }).toString()}`);
const copy = computed(() => {
  switch (failure.value) {
    case "missing":
      return { title: "No expert selected", body: "Open an expert's profile and choose Send a message to start a conversation." };
    case "unavailable":
      return { title: "This expert can't receive messages yet", body: "You can still request a free quote, and they'll reply in your project." };
    default:
      return { title: "We couldn't open the conversation", body: "Please try again in a moment." };
  }
});

async function open() {
  if (!expertSlug.value) {
    failure.value = "missing";
    return;
  }
  if (opening.value) return;
  opening.value = true;
  failure.value = null;
  try {
    const room = await api.directChatrooms.open({ body: { recipient_expert_slug: expertSlug.value } });
    await router.replace(`/client/messages/${room.thread_id}`);
  } catch (error: any) {
    const status = error?.response?.status ?? error?.status;
    failure.value = status === 404 || status === 403 || status === 422 ? "unavailable" : "error";
    if (failure.value === "error") captureConvexError(error, { operation: "chat.open_direct_room_by_slug", level: "error" });
  } finally {
    opening.value = false;
  }
}

onMounted(open);
</script>

<template>
  <main class="flex min-h-0 flex-1 items-center justify-center bg-secondary p-6">
    <UiCard class="w-full max-w-md">
      <div v-if="!failure" class="flex flex-col items-center gap-3 py-6 text-center" role="status" aria-live="polite">
        <div class="rounded-full bg-se-gray-100 p-4"><UiIcon name="chat-bubble" class="h-7 w-7 animate-pulse text-se-gray-600" /></div>
        <UiText as="h1" variant="h5" text="Opening your conversation…" />
        <UiText as="p" variant="p-small" class="text-se-gray-600" text="You'll be able to message the expert directly." />
      </div>
      <div v-else class="flex flex-col items-center gap-4 py-4 text-center" role="alert">
        <div class="rounded-full bg-se-gray-100 p-4"><UiIcon name="chat-bubble" class="h-7 w-7 text-se-gray-500" /></div>
        <div class="flex flex-col gap-1">
          <UiText as="h1" variant="h5" :text="copy.title" />
          <UiText as="p" variant="p-small" class="text-se-gray-600" :text="copy.body" />
        </div>
        <div class="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
          <UiButton v-if="failure === 'error'" label="Try again" :disabled="opening" @click="open" />
          <UiButton v-if="failure === 'unavailable' && expertSlug" label="Get a Free Quote" :href="quoteHref" />
          <UiButton variant="outline" label="Go to Messages" href="/client/messages" />
        </div>
      </div>
    </UiCard>
  </main>
</template>
